// Group G5's own words (progress, history and status): the milestone stations and lifecycle events, the journey and
// rail captions, the event list's titles, the diff's column heads and stepper, the sync strip's states, the state
// banners' keys, the stages' day line and the version words. Each quotes the line of the vendored glossary or
// design-system excerpts it is taken from (labels.test.mjs checks the quote, the text and the pending status). Words
// the shared module already carries (status chains, Days on Hold, Override, Retry …) are imported from ../labels.

import { INTERVIEW_STATUS, PANEL_CHANGE_STATUS, REQUEST_STATUS, REQUISITION_STATUS, ROLE_LABELS, defineLabels, template, type LabelDef, type LabelSource } from "../labels";

const his = <const S extends string>(text: S, source: LabelSource, quote: string) =>
  ({ text, source, quote, status: "his" }) as const satisfies LabelDef;
const pending = <const S extends string>(text: S, source: LabelSource, quote: string) =>
  ({ text, source, quote, status: "pending" }) as const satisfies LabelDef;

const STATIONS = "Request · Approval · Posting · Intake · Panel · Offer · Filled; Hold · Cancel · Reopen · Archive; Offer Accepted · Ready for HRIS · HRIS Hired · Filled · Archived";
const ONE_LIST = "Candidate timeline, Requisition timeline, request history, negotiation History, scheduling history, Audit.";
const JOURNEYS = "*HRBP request creation · HM certification · HRBP approval* · *Load framework · Customize · Validate · Finalize* · *Packet Draft · Generated · Reviewed · Released* · *Change · Validate · Version · Activate · Audit*";
const PARALLEL = "Approval fans out into three parallel tiles (`journey.parallel`): Coordinator creates/posts · TA Manager assigns Recruiter · Compensation starts in parallel.";
const AUDIT_KEYS = "*Old → New · Actor · Timestamp · Reason · Related req / candidate · Evidence · Source*";
const DIFF_WORDS = "*original/revised values* · *Original · Revised* · *Rationale* · *Old → New* · *Reason*";
const SYNC = "*synchronizing* (blue) · *delivered* (green) · *Sync failure* (red";
const HOLD = "On Hold (`banner.warn` + paused / continued activities and dates + Days on Hold)";

/** #38 The requisition timeline: the seven stations of §4.3 and the four lifecycle events drawn on the track (DC-30). */
export const MILESTONE_LABELS = /* @__PURE__ */ defineLabels({
  request: his("Request"),
  approval: his("Approval"),
  posting: his("Posting"),
  intake: his("Intake"),
  panel: his("Panel"),
  offer: his("Offer"),
  filled: his("Filled"),
  hold: his("Hold"),
  cancel: his("Cancel"),
  reopen: his("Reopen"),
  archive: his("Archive"),
});

/** The station that holds the current branch: *On Hold · 4 days* (DC-30). */
export const MILESTONE_TEMPLATES = {
  onHoldDays: /* @__PURE__ */ template(his("On Hold · {n} days")),
} as const;

/**
 * His sentences that name the acting role as a slot: the BRD wrote them for the 8 Oct roles, and DC-228 (as amended
 * 12 Oct 05:29) folds TA Admin and HR Systems Admin into the one Admin, so the role is drawn with the Admin word. His
 * TA Manager sentences stay verbatim (the TA Manager is a role of its own).
 */
export const ROLE_SENTENCE_TEMPLATES = {
  /** Q-E15: who may recover a failed sync. */
  recoveryAvailable: /* @__PURE__ */ template(pending("Recovery is available to {role}")),
} as const;

/** #39 Journey: the parallel caption, and the steps of his journeys (DS #39, DC-64). */
export const JOURNEY_LABELS = /* @__PURE__ */ defineLabels({
  inParallel: his("in parallel"),
  hrbpRequestCreation: his("HRBP request creation"),
  hmCertification: his("HM certification"),
  hrbpApproval: his("HRBP approval"),
  loadFramework: his("Load framework"),
  customize: his("Customize"),
  validate: his("Validate"),
  finalize: his("Finalize"),
  change: his("Change"),
  version: his("Version"),
  activate: his("Activate"),
  audit: his("Audit"),
  coordinatorCreatesPosts: his("Coordinator creates/posts"),
  taManagerAssignsRecruiter: his("TA Manager assigns Recruiter"),
  compensationStartsInParallel: his("Compensation starts in parallel"),
});

/** The step states a progress strip names for a screen reader (the glyph that tells them apart is decorative). A
 * passed-over step is his DS #40 word *skipped*; the shared chains give *Completed*, *Blocked*, *Returned* and *On
 * Hold*. A locked tile and a warn step have no word yet (requested under Q-E15). */
export const PROGRESS_STATE_LABELS = /* @__PURE__ */ defineLabels({
  skipped: his("skipped"),
});

export const PROGRESS_STATE_WORDS = {
  completed: INTERVIEW_STATUS.completed,
  blocked: PANEL_CHANGE_STATUS.blocked,
  returned: REQUEST_STATUS.returned,
  onHold: REQUISITION_STATUS.onHold,
  skipped: PROGRESS_STATE_LABELS.skipped,
} as const;

/** #69 The one event list (DC-20): its names, and the keys an audit row carries. */
export const HISTORY_LABELS = /* @__PURE__ */ defineLabels({
  candidateTimeline: his("Candidate timeline"),
  requisitionTimeline: his("Requisition timeline"),
  history: his("History"),
  audit: his("Audit"),
  actor: his("Actor"),
  timestamp: his("Timestamp"),
  reason: his("Reason"),
  evidence: his("Evidence"),
  source: his("Source"),
});

/** row 108 A security-trimmed row (DC-28). */
export const MASKED_LABELS = /* @__PURE__ */ defineLabels({
  subjectToPermission: his("subject to permission"),
});

/** #70 Diff: the column heads, the reason and rationale, and the locked original's rule. */
export const DIFF_LABELS = /* @__PURE__ */ defineLabels({
  original: his("Original"),
  revised: his("Revised"),
  rationale: his("Rationale"),
  reason: his("Reason"),
  old: his("Old"),
  new: his("New"),
  originalRevisedValues: his("Original/revised values"),
  auditOriginalRevised: his("Audit all original/revised values."),
});

/** The *1 of n* stepper (DS #70, DC-63): the position, and each step button named by where it goes. */
export const DIFF_TEMPLATES = {
  stepOf: /* @__PURE__ */ template(his("{i} of {n}")),
} as const;

/** #82 Sync: the delivery states, the strip's name and the recovery behind the matrix Override. */
const SYNC_WORDS = /* @__PURE__ */ defineLabels({
  synchronizing: his("synchronizing"),
  delivered: his("delivered"),
  deliveryStatus: his("Delivery status"),
  manualRecovery: his("Manual recovery"),
});
export const SYNC_LABELS = Object.freeze({
  ...SYNC_WORDS,
  recoveryAvailable: ROLE_SENTENCE_TEMPLATES.recoveryAvailable({ role: ROLE_LABELS.admin }),
});

/** #83 State banners: the On Hold keys, the closure's reporting category and the reopen request. */
export const STATE_BANNER_LABELS = /* @__PURE__ */ defineLabels({
  paused: his("paused"),
  continued: his("continued"),
  reportingCategory: his("Reporting category"),
  requestReopen: his("Request reopen"),
  taManagerNotified: his("TA Manager notified"),
  reopenRequestRule: his("HM or TA team may request hold/cancel/reopen; HRBP approves; TA Manager notified."),
});

/** row 117 Stages: a segment's Active Time in days (his portfolio word), one day in the singular. */
const ACTIVE_DAYS = "| **Active Time** / active days | same; \"SLA uses Active Time.\" Portfolio \"active days\".";
export const STAGES_TEMPLATES = {
  activeDays: /* @__PURE__ */ template(his("{n} active days")),
  activeDay: /* @__PURE__ */ template(his("{n} active day")),
} as const;

/** row 118 / row 119 Versions: the mono version number and the newest version's tag (pending, Q-E16). */
export const VERSION_LABELS = /* @__PURE__ */ defineLabels({
  current: pending("Current"),
  originalPostingType: his("original posting type"),
  originalPostingTypeField: his("Original posting type"),
  currentPostingStrategy: his("current posting strategy"),
});

export const VERSION_TEMPLATES = {
  version: /* @__PURE__ */ template(his("v{n}")),
} as const;
